import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { FeatureFlag } from 'types/features';
import { ExternalStatusType, InvoiceData, InvoiceStatusType } from 'types/invoices';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

/**
 * Whether General shows Existing payments: on a debit with one payer, while the invoice edits or while a paid or sent
 * one may still have a prepayment removed. Prepayments are applied per payer once the invoice is split, so there is
 * nothing to apply before that.
 */
export const useShowsExistingPayments = (invoice: InvoiceData): boolean => {
    const { isDebit } = useInvoice(invoice);
    const canRemovePrepaymentFromPaid = useHasPermissions(['UPDATE.INVOICES.STATE_PAID.REMOVE_PREPAYMENT']);
    const canRemovePrepaymentFromSent = useHasPermissions(['UPDATE.INVOICES.STATE_SENT.REMOVE_PREPAYMENT']);
    const { hasMultiplePayers } = useSharedPayment(invoice);
    return (
        isDebit &&
        !hasMultiplePayers &&
        (invoice.isEditable ||
            (canRemovePrepaymentFromPaid && invoice.invoiceStatusTypeCode === InvoiceStatusType.PAID) ||
            (canRemovePrepaymentFromSent && invoice.invoiceStatusTypeCode === InvoiceStatusType.SENT))
    );
};

/** Whether General shows KSeF details: with external approval on, once the invoice has an external status to show. */
export const useShowsKsefDetails = (invoice: InvoiceData): boolean => {
    const isExternalApprovalEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_EXTERNAL_APPROVAL);
    return (
        isExternalApprovalEnabled &&
        !!invoice.externalStatusTypeCode &&
        invoice.externalStatusTypeCode !== ExternalStatusType.IGNORE
    );
};
